import { z } from "zod";
/**
 * Oyuncu geri bildirimi — oyun ↔ sunucu ↔ konsol sözleşmesi (tek kaynak).
 *
 * Sözleşmenin insan okur hali: Cinderpath `design/ekip/geri-bildirim.md`.
 * Buradaki sınırlar değişirse o dosya da değişir (oyun tarafı ona göre yazılıyor).
 *
 * Kimlik: hesap yok. Oyun cihazda rastgele bir yazar kimliği + gizli bir yazar
 * anahtarı üretir ve her istekte başlıkla gönderir. Sunucu ilk YAZMA isteğinde
 * yazarı kaydeder (anahtarın HMAC'i), sonra eşleşme ister. IP saklanmaz.
 */
export const FEEDBACK_LIMITS = {
    textMin: 10,
    textMax: 1000,
    messageMin: 1,
    messageMax: 1000,
    replyMax: 2000,
    reasonMax: 300,
    heroNameMax: 32,
    rankTextMax: 32,
    /** Yazar başına 24 saatte en fazla gönderi. */
    postsPerDay: 5,
    /** Yazar başına 24 saatte en fazla yazışma mesajı (tüm gönderiler toplamı). */
    messagesPerDay: 30,
    /** Tek gönderinin yazışmasında en fazla mesaj (iki taraf toplamı). */
    messagesPerThread: 200,
    pageSize: 20,
    /** Pano sayfalamasında en derin kayma (kötü niyetli tarama sınırı). */
    maxOffset: 2000,
    /** Yorum metni (kırpılmış). */
    commentMin: 2,
    commentMax: 500,
    /** Yazar başına 24 saatte en fazla yorum. */
    commentsPerDay: 30,
    /** Yazar başına dakikada en fazla yorum. */
    commentsPerMinute: 6,
    commentPageSize: 30,
    /** Bu kadar FARKLI kişi bildirince yorum kendiliğinden gizlenir (konsolda incelemede). */
    commentHideReports: 3,
};
/** Geliştiricinin konsoldan yazdığı yorumda görünen ad. */
export const FEEDBACK_DEV_NAME = "GWR Labs";
export const FEEDBACK_AUTHOR_HEADER = "x-feedback-author";
export const FEEDBACK_TOKEN_HEADER = "x-feedback-token";
/** Yazar kimliği: UUID ya da benzeri. */
export const feedbackAuthorIdSchema = z.string().regex(/^[A-Za-z0-9-]{8,64}$/);
/** Yazar anahtarı: en az 16 karakter (UUID uygun). */
export const feedbackTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{16,128}$/);
export const FEEDBACK_CATEGORIES = ["bug", "idea", "other"];
export const FEEDBACK_STATUSES = ["pending", "open", "planned", "done", "rejected"];
/** Panoda (herkese açık) görünen durumlar. */
export const FEEDBACK_PUBLIC_STATUSES = ["open", "planned", "done"];
export function isPublicStatus(status) {
    return FEEDBACK_PUBLIC_STATUSES.includes(status);
}
// Kontrol karakterleri (\n ve \t hariç) ve yön değiştiren Unicode işaretleri
// (U+202A–202E, U+2066–2069): metni ekranda başka türlü gösterip kandırmaya yarar.
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F‎‏‪-‮⁦-⁩]/g;
/**
 * Düz metin temizliği. HTML olarak hiçbir yerde işlenmez (oyun Text, konsol React
 * metin düğümü), bu yüzden kaçış değil temizlik yapılır: satır sonları tekleşir,
 * kontrol karakterleri atılır, üçten fazla boş satır ikiye iner, baş/son boşluk kırpılır.
 */
export function cleanFeedbackText(input) {
    return input
        .replace(/\r\n?/g, "\n")
        .replace(CONTROL_RE, "")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
const cleanText = (min, max) => z
    .string()
    .max(max * 2) // temizlikten önce kaba üst sınır (dev gövde zod'u yormasın)
    .transform(cleanFeedbackText)
    .pipe(z.string().min(min).max(max));
/** Rütbe: basamak numarası (önerilen; istemci kendi dilinde adlandırır) ya da kısa metin. */
export const feedbackRankSchema = z.union([
    z.number().int().min(0).max(999),
    z.string().transform(cleanFeedbackText).pipe(z.string().max(FEEDBACK_LIMITS.rankTextMax)),
]);
export const feedbackCreateSchema = z.object({
    category: z.enum(FEEDBACK_CATEGORIES),
    text: cleanText(FEEDBACK_LIMITS.textMin, FEEDBACK_LIMITS.textMax),
    heroName: z
        .string()
        .transform(cleanFeedbackText)
        .pipe(z.string().min(1).max(FEEDBACK_LIMITS.heroNameMax)),
    rank: feedbackRankSchema,
    appVersion: z.string().max(20).optional(),
    locale: z.string().max(12).optional(),
});
/** Panodaki gönderiye yorum (onaysız, hemen görünür). */
export const feedbackCommentCreateSchema = z.object({
    text: cleanText(FEEDBACK_LIMITS.commentMin, FEEDBACK_LIMITS.commentMax),
    heroName: z
        .string()
        .transform(cleanFeedbackText)
        .pipe(z.string().min(1).max(FEEDBACK_LIMITS.heroNameMax)),
    rank: feedbackRankSchema,
    appVersion: z.string().max(20).optional(),
    locale: z.string().max(12).optional(),
});
/** Yorum sayfası imleci: opak (base64url "ms.uuid"); bozuksa baştan başlanır. */
export const feedbackCommentQuerySchema = z.object({
    cursor: z.string().max(120).optional().catch(undefined),
});
export const feedbackAdminCommentSchema = z.object({
    text: cleanText(FEEDBACK_LIMITS.commentMin, FEEDBACK_LIMITS.replyMax),
});
export const feedbackMessageInputSchema = z.object({
    text: cleanText(FEEDBACK_LIMITS.messageMin, FEEDBACK_LIMITS.messageMax),
});
export const feedbackReportSchema = z.object({
    reason: z
        .string()
        .max(FEEDBACK_LIMITS.reasonMax * 2)
        .transform(cleanFeedbackText)
        .pipe(z.string().max(FEEDBACK_LIMITS.reasonMax))
        .default(""),
});
export const feedbackBoardQuerySchema = z.object({
    sort: z.enum(["top", "new"]).catch("top").default("top"),
    cursor: z
        .string()
        .regex(/^\d{1,6}$/)
        .transform(Number)
        .pipe(z.number().int().min(0).max(FEEDBACK_LIMITS.maxOffset))
        .optional()
        .catch(undefined),
});
// --- Konsol (yönetici) -------------------------------------------------------
export const feedbackAdminTabSchema = z.enum(["pending", "board", "reported", "rejected", "all"]);
export const feedbackAdminStatusSchema = z.enum(FEEDBACK_PUBLIC_STATUSES);
export const feedbackReplySchema = z.object({
    text: cleanText(1, FEEDBACK_LIMITS.replyMax),
});
export const feedbackAdminMessageSchema = z.object({
    text: cleanText(1, FEEDBACK_LIMITS.replyMax),
});
export const feedbackAdminCommentFilterSchema = z.enum(["flagged"]);
//# sourceMappingURL=feedback.js.map